import { useState, useEffect, useRef, useCallback } from 'react';
import { Calculator as CalcIcon, Delete } from 'lucide-react';
import ModalCloseButton from './ModalCloseButton';

/**
 * CalculatorModal — basic four-function calculator with a percent-of
 * operation, mounted from the Capital Tracker card on the Portfolio
 * page. Closes ONLY via the X button (no overlay-click dismiss, no
 * Esc) per user request.
 *
 * Operations: + − × ÷  and  %  (a % b ⇒ a × b / 100, i.e. "X% of Y").
 *
 * Mobile: near-fullscreen, fixed at center.
 * Desktop: compact panel that the user can drag by its header.
 */
export default function CalculatorModal({ open, onClose }) {
  // Reset on each open so a fresh launch always starts blank.
  const [display, setDisplay] = useState('0');
  const [prev, setPrev] = useState(null);
  const [op, setOp] = useState(null);
  const [awaitingOperand, setAwaitingOperand] = useState(false);

  // Desktop draggable position. `null` = use the default centered
  // placement (CSS-driven). Once the user starts dragging we switch to
  // pixel-coordinated positioning.
  const [pos, setPos] = useState(null);
  const dragState = useRef(null);

  // Reset state every time the modal opens.
  useEffect(() => {
    if (open) {
      setDisplay('0');
      setPrev(null);
      setOp(null);
      setAwaitingOperand(false);
    }
  }, [open]);

  // --- Calculator engine ----------------------------------------------------
  const inputDigit = useCallback((d) => {
    setDisplay((cur) => {
      if (awaitingOperand) {
        setAwaitingOperand(false);
        return String(d);
      }
      return cur === '0' ? String(d) : cur + d;
    });
  }, [awaitingOperand]);

  const inputDot = useCallback(() => {
    if (awaitingOperand) {
      setDisplay('0.');
      setAwaitingOperand(false);
      return;
    }
    setDisplay((cur) => (cur.includes('.') ? cur : cur + '.'));
  }, [awaitingOperand]);

  const clearAll = useCallback(() => {
    setDisplay('0');
    setPrev(null);
    setOp(null);
    setAwaitingOperand(false);
  }, []);

  const backspace = useCallback(() => {
    setDisplay((cur) => {
      if (awaitingOperand) return cur;
      if (cur.length <= 1 || (cur.length === 2 && cur.startsWith('-'))) return '0';
      return cur.slice(0, -1);
    });
  }, [awaitingOperand]);

  const toggleSign = useCallback(() => {
    setDisplay((cur) => {
      if (cur === '0') return cur;
      return cur.startsWith('-') ? cur.slice(1) : '-' + cur;
    });
  }, []);

  const apply = (a, b, operator) => {
    switch (operator) {
      case '+': return a + b;
      case '-': return a - b;
      case '*': return a * b;
      case '/': return b === 0 ? NaN : a / b;
      // Percent-of: a % b ⇒ a × b / 100.
      // Example: 50 % 200 = "50% of 200" = 100.
      case '%': return (a * b) / 100;
      default:  return b;
    }
  };

  const performOp = useCallback((nextOp) => {
    const current = parseFloat(display);
    if (prev == null) {
      setPrev(current);
    } else if (op) {
      const result = apply(prev, current, op);
      const rounded = Number.isFinite(result) ? Math.round(result * 1e10) / 1e10 : result;
      setDisplay(String(rounded));
      setPrev(rounded);
    }
    setOp(nextOp);
    setAwaitingOperand(true);
  }, [display, prev, op]);

  const equals = useCallback(() => {
    if (prev == null || op == null) return;
    const current = parseFloat(display);
    const result = apply(prev, current, op);
    const rounded = Number.isFinite(result) ? Math.round(result * 1e10) / 1e10 : result;
    setDisplay(String(rounded));
    setPrev(null);
    setOp(null);
    setAwaitingOperand(true);
  }, [display, prev, op]);

  // --- Keyboard support -----------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // Don't hijack keys when user is typing in another input on the
      // page (the calculator modal has no inputs, so anything coming
      // through here is meant for us).
      const k = e.key;
      if (/^[0-9]$/.test(k)) { e.preventDefault(); inputDigit(parseInt(k, 10)); return; }
      if (k === '.') { e.preventDefault(); inputDot(); return; }
      if (k === '+' || k === '-' || k === '*' || k === '/' || k === '%') { e.preventDefault(); performOp(k); return; }
      if (k === 'Enter' || k === '=') { e.preventDefault(); equals(); return; }
      if (k === 'Backspace') { e.preventDefault(); backspace(); return; }
      if (k === 'Escape') { /* user wants X-only dismissal */ return; }
      if (k === 'c' || k === 'C') { e.preventDefault(); clearAll(); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, inputDigit, inputDot, performOp, equals, backspace, clearAll]);

  // --- Desktop drag handlers ------------------------------------------------
  const onDragStart = (e) => {
    if (window.innerWidth < 640) return; // mobile: don't drag
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    dragState.current = {
      offsetX: (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left,
      offsetY: (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top,
      width: rect.width,
      height: rect.height,
    };
    const onMove = (ev) => {
      const x = (ev.clientX ?? ev.touches?.[0]?.clientX ?? 0) - dragState.current.offsetX;
      const y = (ev.clientY ?? ev.touches?.[0]?.clientY ?? 0) - dragState.current.offsetY;
      const maxX = window.innerWidth - dragState.current.width;
      const maxY = window.innerHeight - dragState.current.height;
      setPos({
        x: Math.max(0, Math.min(maxX, x)),
        y: Math.max(0, Math.min(maxY, y)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      dragState.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  };

  if (!open) return null;

  // Format display: large numbers get grouped, plain otherwise. We use
  // toLocaleString cautiously so a trailing "." while typing stays.
  const displayPretty = (() => {
    if (display === 'NaN' || display === 'Infinity' || display === '-Infinity') return 'Error';
    if (display.endsWith('.')) return display;
    const n = Number(display);
    if (!Number.isFinite(n)) return display;
    return n.toLocaleString('en-US', { maximumFractionDigits: 10 });
  })();

  // Operator labels (display ≠ engine code)
  const opLabel = (o) => ({ '+': '+', '-': '−', '*': '×', '/': '÷', '%': '%' }[o] || '');

  const Btn = ({ children, onClick, variant = 'default', testId, className = '' }) => {
    const variants = {
      default: 'bg-muted hover:bg-muted/70 text-foreground',
      op: 'bg-amber-100 hover:bg-amber-200 dark:bg-amber-950/60 dark:hover:bg-amber-900/60 text-amber-800 dark:text-amber-200 font-bold',
      opActive: 'bg-amber-500 text-white font-bold',
      equals: 'bg-fuchsia-700 hover:bg-fuchsia-800 dark:bg-amber-200 dark:hover:bg-amber-100 dark:text-stone-900 text-white font-bold',
      clear: 'bg-rose-100 hover:bg-rose-200 dark:bg-rose-950/50 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 font-semibold',
    };
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={`h-12 sm:h-11 rounded-lg text-base transition-colors active:scale-95 ${variants[variant]} ${className}`}
      >
        {children}
      </button>
    );
  };

  const desktopStyle = pos
    ? { left: pos.x, top: pos.y, transform: 'none' }
    : undefined;

  return (
    <div
      className="fixed inset-0 z-[400] sm:pointer-events-none"
      data-testid="calculator-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Calculator"
    >
      <div
        // Mobile: near-fullscreen centered. Desktop: compact panel,
        // centered by default until the user drags it. On desktop
        // we re-enable pointer-events on the panel only so the rest
        // of the screen stays clickable — users can read AND tap on
        // portfolio numbers while the calculator floats on top.
        className="absolute bg-background rounded-xl border shadow-2xl flex flex-col
                   inset-2 sm:inset-auto sm:pointer-events-auto
                   sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2
                   sm:w-[320px]"
        style={desktopStyle}
        data-testid="calculator-modal"
      >
        {/* Header — also the drag handle on desktop. */}
        <div
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/40 rounded-t-xl sm:cursor-grab sm:active:cursor-grabbing select-none"
          data-testid="calculator-modal-header"
        >
          <div className="flex items-center gap-2">
            <CalcIcon className="w-4 h-4 text-fuchsia-900 dark:text-amber-100" />
            <h3 className="text-sm font-bold">Calculator</h3>
            <span className="hidden sm:inline text-[10px] text-muted-foreground/70 ml-1">drag to move</span>
          </div>
          <ModalCloseButton onClick={onClose} testId="calculator-modal-close" />
        </div>

        {/* Display */}
        <div className="px-4 py-3 bg-card border-b">
          {prev != null && op && (
            <div className="text-right text-[11px] text-muted-foreground font-mono h-4" data-testid="calculator-prev">
              {Number(prev).toLocaleString('en-US', { maximumFractionDigits: 10 })} {opLabel(op)}
            </div>
          )}
          <div
            className="text-right text-3xl sm:text-2xl font-mono font-bold tracking-tight break-all min-h-[2.2rem]"
            data-testid="calculator-display"
          >
            {displayPretty}
          </div>
        </div>

        {/* Buttons */}
        <div className="p-3 grid grid-cols-4 gap-2">
          <Btn variant="clear" onClick={clearAll} testId="calculator-btn-clear">AC</Btn>
          <Btn onClick={toggleSign} testId="calculator-btn-sign">±</Btn>
          <Btn variant={op === '%' ? 'opActive' : 'op'} onClick={() => performOp('%')} testId="calculator-btn-percent">%</Btn>
          <Btn variant={op === '/' ? 'opActive' : 'op'} onClick={() => performOp('/')} testId="calculator-btn-divide">÷</Btn>

          <Btn onClick={() => inputDigit(7)} testId="calculator-btn-7">7</Btn>
          <Btn onClick={() => inputDigit(8)} testId="calculator-btn-8">8</Btn>
          <Btn onClick={() => inputDigit(9)} testId="calculator-btn-9">9</Btn>
          <Btn variant={op === '*' ? 'opActive' : 'op'} onClick={() => performOp('*')} testId="calculator-btn-multiply">×</Btn>

          <Btn onClick={() => inputDigit(4)} testId="calculator-btn-4">4</Btn>
          <Btn onClick={() => inputDigit(5)} testId="calculator-btn-5">5</Btn>
          <Btn onClick={() => inputDigit(6)} testId="calculator-btn-6">6</Btn>
          <Btn variant={op === '-' ? 'opActive' : 'op'} onClick={() => performOp('-')} testId="calculator-btn-minus">−</Btn>

          <Btn onClick={() => inputDigit(1)} testId="calculator-btn-1">1</Btn>
          <Btn onClick={() => inputDigit(2)} testId="calculator-btn-2">2</Btn>
          <Btn onClick={() => inputDigit(3)} testId="calculator-btn-3">3</Btn>
          <Btn variant={op === '+' ? 'opActive' : 'op'} onClick={() => performOp('+')} testId="calculator-btn-plus">+</Btn>

          <Btn onClick={() => inputDigit(0)} testId="calculator-btn-0" className="col-span-2">0</Btn>
          <Btn onClick={inputDot} testId="calculator-btn-dot">.</Btn>
          <Btn variant="equals" onClick={equals} testId="calculator-btn-equals">=</Btn>

          <button
            type="button"
            onClick={backspace}
            data-testid="calculator-btn-backspace"
            className="col-span-4 h-9 rounded-lg text-xs font-semibold bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98]"
          >
            <Delete className="w-3.5 h-3.5" /> Backspace
          </button>
        </div>

        {/* Tiny help footer */}
        <div className="px-3 pb-2 text-[10px] text-muted-foreground/70 leading-snug">
          % = "X% of Y" (e.g. 50 % 200 = 100). Keyboard: 0–9, +, −, *, /, %, Enter, Backspace, C.
        </div>
      </div>
    </div>
  );
}
